import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Book, Users, FileText, Plus, Bookmark, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import AuthContext from '../../contexts/AuthContext';
import SearchBar from '../../components/SearchBar';

/**
 * School Home Page component
 * Shows dashboard with quick access to courses, materials, and professors
 */
const HomePage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    courseCount: 0,
    professorCount: 0,
    materialCount: 0,
  });
  const [recentMaterials, setRecentMaterials] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch stats, recent materials, and subscriptions
        const [statsRes, materialsRes, subscriptionsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/schools/${user.schoolId}/stats`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/schools/${user.schoolId}/materials/recent`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/users/${user.id}/subscriptions`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        ]);
        
        // Process responses
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
        
        if (materialsRes.ok) {
          const materialsData = await materialsRes.json();
          setRecentMaterials(materialsData);
        }
        
        if (subscriptionsRes.ok) {
          const subscriptionsData = await subscriptionsRes.json();
          setSubscriptions(subscriptionsData);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    if (user?.schoolId) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);
  
  return (
    <div className="container mx-auto px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Welcome to {user?.school?.name || 'Your School'}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Access courses, materials, and connect with professors
        </p>
      </div>
      
      {/* Search box */}
      <div className="mb-8 bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
          Quick Search
        </h2>
        <SearchBar />
      </div>
      
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-500 dark:text-blue-300 mr-4">
              <Book size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Courses
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {loading ? '...' : stats.courseCount}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Link 
              to="/courses" 
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              View all courses
            </Link>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-500 dark:text-purple-300 mr-4">
              <Users size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Professors
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {loading ? '...' : stats.professorCount}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Link 
              to="/professors" 
              className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
            >
              View all professors
            </Link>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900 text-green-500 dark:text-green-300 mr-4">
              <FileText size={24} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Materials
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {loading ? '...' : stats.materialCount}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Link 
              to="/my-materials" 
              className="text-sm text-green-600 dark:text-green-400 hover:underline"
            >
              View my materials
            </Link>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent materials */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Recent Materials
            </h2>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Clock size={16} className="mr-1" />
              Latest uploads
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
            </div>
          ) : recentMaterials.length > 0 ? (
            <div className="space-y-4">
              {recentMaterials.map((material) => (
                <div 
                  key={material.id} 
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-start">
                    <div 
                      className={`p-2 rounded-md mr-3 ${
                        material.type === 'notes'
                          ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
                          : material.type === 'exam'
                          ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300'
                          : material.type === 'syllabus'
                          ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      <FileText size={20} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {material.title}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {material.courseName} • {material.userName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {new Date(material.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <Link
                      to={`/courses/${material.courseId}/sections/${material.sectionId}`}
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      View material
                    </Link>
                  </div>
                </div>
              ))}
              
              <div className="text-center mt-4">
                <Link
                  to="/courses"
                  className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:underline"
                >
                  <Plus size={16} className="mr-1" />
                  Browse more materials
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              <FileText size={40} className="mx-auto mb-2 opacity-40" />
              <p>No materials have been uploaded yet</p>
              <p className="text-sm mt-2">
                Be the first to contribute by uploading course materials
              </p>
            </div>
          )}
        </div>
        
        {/* My subscriptions */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              My Courses
            </h2>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Bookmark size={16} className="mr-1" />
              Subscribed courses
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center p-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
            </div>
          ) : subscriptions.length > 0 ? (
            <div className="space-y-4">
              {subscriptions.map((subscription) => (
                <div 
                  key={subscription.id} 
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex items-start">
                    <div className="p-2 rounded-md mr-3 bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300">
                      <Book size={20} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {subscription.courseCode}: {subscription.courseName}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {subscription.professorName} • {subscription.semester} {subscription.year}
                      </p>
                      <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-500">
                        <span className="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                          {subscription.departmentCode}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2">
                    <Link
                      to={`/courses/${subscription.courseId}/sections/${subscription.sectionId}`}
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      View course
                    </Link>
                  </div>
                </div>
              ))}
              
              <div className="text-center mt-4">
                <Link
                  to="/my-courses"
                  className="inline-flex items-center text-sm text-red-600 dark:text-red-400 hover:underline"
                >
                  View all my courses
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              <Book size={40} className="mx-auto mb-2 opacity-40" />
              <p>You haven't subscribed to any courses yet</p>
              <Link
                to="/courses"
                className="inline-flex items-center mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                <Plus size={16} className="mr-1" />
                Browse courses
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;